import { useEffect, useState } from 'react';
import './AdaptiveLogo.css';

type LogoShape = 'pending' | 'circle' | 'square' | 'icon';

type AdaptiveLogoProps = {
    src: string;
    alt?: string;
    className?: string;
    imgClassName?: string;
    /** Used only when image pixels cannot be read (CORS). */
    fallbackShape?: 'circle' | 'square';
};

const PLACEHOLDER_PATTERNS = [
    'college_icon',
    'no_img',
    'no_university_img',
    'briefcase',
    'home-icon',
    'icon-note',
    'tea-cup',
    'yellow-student',
];

const isPlaceholderSrc = (src: string) =>
    PLACEHOLDER_PATTERNS.some((pattern) => (src || '').toLowerCase().includes(pattern));

const isNearWhite = (r: number, g: number, b: number) => r > 242 && g > 242 && b > 242;

/**
 * Decide frame from the image itself:
 * 1) Aspect ratio — wide/tall → square frame
 * 2) Corner emptiness — transparent/white corners + center content → circular logo → circle frame
 * 3) Otherwise → square frame
 */
const detectLogoShape = (img: HTMLImageElement, fallback: 'circle' | 'square'): 'circle' | 'square' => {
    try {
        const nw = img.naturalWidth || 1;
        const nh = img.naturalHeight || 1;
        const ratio = nw / nh;

        if (ratio > 1.2 || ratio < 0.83) return 'square';

        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return fallback;

        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);

        const emptiness = (x: number, y: number, w = 5, h = 5) => {
            const { data } = ctx.getImageData(x, y, w, h);
            let empty = 0;
            const pixels = data.length / 4;
            for (let i = 0; i < data.length; i += 4) {
                const a = data[i + 3];
                if (a < 40 || isNearWhite(data[i], data[i + 1], data[i + 2])) empty += 1;
            }
            return empty / pixels;
        };

        const centerContentRatio = () => {
            const { data } = ctx.getImageData(size / 2 - 10, size / 2 - 10, 20, 20);
            let content = 0;
            const pixels = data.length / 4;
            for (let i = 0; i < data.length; i += 4) {
                const a = data[i + 3];
                if (a > 40 && !isNearWhite(data[i], data[i + 1], data[i + 2])) content += 1;
            }
            return content / pixels;
        };

        const cornerEmptyAvg =
            (emptiness(0, 0) +
                emptiness(size - 5, 0) +
                emptiness(0, size - 5) +
                emptiness(size - 5, size - 5)) /
            4;

        if (cornerEmptyAvg >= 0.65 && centerContentRatio() >= 0.12) return 'circle';
        return 'square';
    } catch {
        return fallback;
    }
};

const AdaptiveLogo = ({
    src,
    alt = '',
    className = '',
    imgClassName = '',
    fallbackShape = 'square',
}: AdaptiveLogoProps) => {
    const [shape, setShape] = useState<LogoShape>(isPlaceholderSrc(src) ? 'icon' : 'pending');

    useEffect(() => {
        // Default placeholder icons (college building, etc.) — dedicated non-clipping frame
        if (isPlaceholderSrc(src)) {
            setShape('icon');
            return;
        }

        setShape('pending');
        if (!src) {
            setShape(fallbackShape);
            return;
        }

        let cancelled = false;
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
            if (!cancelled) setShape(detectLogoShape(probe, fallbackShape));
        };
        probe.onerror = () => {
            if (!cancelled) setShape(fallbackShape);
        };
        probe.src = src;

        return () => {
            cancelled = true;
            probe.onload = null;
            probe.onerror = null;
        };
    }, [src, fallbackShape]);

    return (
        <div className={`${className} logo-shape-${shape}`.trim()}>
            <img src={src} alt={alt} className={imgClassName} />
        </div>
    );
};

export default AdaptiveLogo;
